import type { LineItem } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { gql, useLazyQuery, useQuery } from "@apollo/client";

export const useGetAllLineItems = (
  invoiceId: string,
  filters?: {
    createdBy?: "manual" | "extraction";
    deletedAt?: { value: Date; op: string };
  },
  options: {
    paranoid?: boolean;
  } = {
    paranoid: true,
  },
) => {
  const { tenantId } = useTenant();
  const { paranoid } = options;

  const filter = filters
    ? {
        ...(filters?.createdBy
          ? { createdBy: { value: filters.createdBy, op: "eq" } }
          : {}),
        ...(filters?.deletedAt ? { deletedAt: filters.deletedAt } : {}),
      }
    : undefined;

  const { data, error, loading, refetch } = useQuery<{
    lineItemDetails: { lineItem: LineItem }[];
  }>(LINE_ITEMS_QUERY, {
    variables: { invoiceId, tenantId, filter, paranoid },
  });

  const handleRefetch = () => {
    return refetch({ invoiceId, tenantId, filter, paranoid });
  };

  return {
    lineItems: data?.lineItemDetails ?? [],
    errorLineItems: error,
    loadingLineItems: loading,
    refetch: handleRefetch,
  };
};

export const useLazyGetAllLineItems = (
  filters?: {
    createdBy?: "manual" | "extraction";
    deletedAt?: { value: Date; op: string };
  },
  options: {
    paranoid?: boolean;
    fetchPolicy?: "network-only" | "cache-first";
    signal?: AbortSignal;
  } = {
    paranoid: true,
    fetchPolicy: "cache-first",
  },
) => {
  const { tenantId } = useTenant();
  const { paranoid } = options;

  const filter = filters
    ? {
        ...(filters?.createdBy
          ? { createdBy: { value: filters.createdBy, op: "eq" } }
          : {}),
        ...(filters?.deletedAt ? { deletedAt: filters.deletedAt } : {}),
      }
    : undefined;

  const [fetchLineItems] = useLazyQuery(LINE_ITEMS_QUERY, {
    fetchPolicy: options.fetchPolicy,
    context: {
      fetchOptions: {
        signal: options.signal,
      },
    },
  });

  const fetch = (invoiceId: string, options?: { signal?: AbortSignal }) => {
    return fetchLineItems({
      variables: { invoiceId, tenantId, filter, paranoid },
      context: {
        fetchOptions: {
          signal: options?.signal,
        },
      },
    });
  };

  return {
    fetch,
  };
};

export const LINE_ITEMS_QUERY = gql`
  query LineItemDetails(
    $invoiceId: String!
    $paranoid: Boolean
    $filter: LineItemFilter
  ) {
    lineItemDetails(
      invoiceId: $invoiceId
      filter: $filter
      paranoid: $paranoid
    ) {
      lineItemDetailsId
      lineItem {
        lineItemId
        costCenterId
        aiDescription
        amount
        nettoAmount
        invoiceId
        name
        vatRate
        startDate
        endDate
        accountJustification
        index
        description
        accountConfidence
        vatRateConfidence
        accountReviewed
        accountId
        amountConfidence
        taxCodeId
        buJustification
        taxCodeConfidence
        bookingText
        createdBy
        createdAt
        unlinked
      }
      costCenter {
        costCenterId
        name
        description
        identifier
      }
      expenseAccount {
        accountId
        accountNo
        accountName
        accountDescription
        accountType
        automatic
        parentAccountId
        min
        max
        buExport
        serviceDateExport
        faktor2
      }
      taxCode {
        taxCodeId
        name
        description
        datevBu
        vatRate
      }
    }
  }
`;
